import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import FeedbackAcknowledgement from './FeedbackAcknowledgement';
import SignUpPrompt from './SignUpPrompt';
import SignUpSuccess from './SignUpSuccess';
import SurveyContainer from './SurveyContainer';
import SurveyForm from './SurveyForm';
import useEscapeToDismiss from './useEscapeToDismiss';
export var DismissTrigger = /*#__PURE__*/function (DismissTrigger) {
  DismissTrigger["AutoDismiss"] = "AUTO_DISMISS";
  DismissTrigger["Manual"] = "MANUAL";
  DismissTrigger["Finished"] = "FINISHED";
  DismissTrigger["Unmount"] = "UNMOUNT";
  return DismissTrigger;
}({});
export var AUTO_DISAPPEAR_DURATION = 8000;
export default (function (_ref) {
  var question = _ref.question,
    statement = _ref.statement,
    onDismiss = _ref.onDismiss,
    onSubmit = _ref.onSubmit,
    onMailingListAnswer = _ref.onMailingListAnswer,
    getUserHasAnsweredMailingList = _ref.getUserHasAnsweredMailingList,
    _ref$textLabel = _ref.textLabel,
    textLabel = _ref$textLabel === void 0 ? 'Why did you give that rating' : _ref$textLabel,
    _ref$textPlaceholder = _ref.textPlaceholder,
    textPlaceholder = _ref$textPlaceholder === void 0 ? 'Tell us why' : _ref$textPlaceholder;
  var autoDisappearTimeoutRef = useRef(null);

  // only allow a single dismiss for a component
  var isDismissedRef = useRef(false);
  var tryDismiss = useCallback(function (trigger) {
    // Already called dismiss once
    if (isDismissedRef.current) {
      return;
    }
    isDismissedRef.current = true;
    onDismissRef.current({
      trigger: trigger
    });
  }, []);
  var _useState = useState('SURVEY'),
    _useState2 = _slicedToArray(_useState, 2),
    currentStep = _useState2[0],
    setCurrentStep = _useState2[1];
  var trySetCurrentStep = useCallback(function (step) {
    // Already dismissed - cannot update the step
    if (isDismissedRef.current) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log("not setting step \"".concat(step, "\" as survey is already dismissed"));
      }
      return;
    }
    setCurrentStep(step);
  }, [setCurrentStep]);

  // using a ref so that we don't break all of our caches if a consumer is using an arrow function
  var onDismissRef = useRef(onDismiss);
  useEffect(function () {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  // reset isDismissedRef.current on mount to be compatible with double render in react18 strict mode
  useEffect(function () {
    isDismissedRef.current = false;
  }, []);
  var tryClearTimeout = useCallback(function () {
    var id = autoDisappearTimeoutRef.current;
    if (id) {
      clearTimeout(id);
      autoDisappearTimeoutRef.current = null;
    }
  }, []);

  // Cleanup any auto dismiss after dismiss
  useEffect(function () {
    return function unmount() {
      tryClearTimeout();
      tryDismiss(DismissTrigger.Unmount);
    };
  }, [tryClearTimeout, tryDismiss]);
  var onSurveySubmit = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(formValues, form) {
      var callback,
        userHasAnswered,
        _args = arguments;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            callback = _args.length > 2 && _args[2] !== undefined ? _args[2] : function () {};
            _context.next = 3;
            return onSubmit(formValues);
          case 3:
            if (!isDismissedRef.current) {
              _context.next = 6;
              break;
            }
            callback();
            return _context.abrupt("return");
          case 6:
            if (formValues.canContact) {
              _context.next = 10;
              break;
            }
            callback();
            trySetCurrentStep('POST_SURVEY_NO_CONSENT');
            return _context.abrupt("return");
          case 10:
            _context.next = 12;
            return getUserHasAnsweredMailingList();
          case 12:
            userHasAnswered = _context.sent;
            callback();

            // Not entering phase 2: user has already answered this question
            if (!userHasAnswered) {
              _context.next = 17;
              break;
            }
            trySetCurrentStep('POST_SURVEY_HAS_SIGN_UP');
            return _context.abrupt("return");
          case 17:
            // Enter phase 2
            trySetCurrentStep('SIGN_UP_PROMPT');
          case 18:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function (_x, _x2) {
      return _ref2.apply(this, arguments);
    };
  }(), [getUserHasAnsweredMailingList, onSubmit, trySetCurrentStep]);
  var onMailingListResponse = useCallback( /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(answer) {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 2;
            return onMailingListAnswer(answer);
          case 2:
            if (!answer) {
              _context2.next = 5;
              break;
            }
            trySetCurrentStep('SIGN_UP_SUCCESS');
            return _context2.abrupt("return");
          case 5:
            // We have already thanked to user, we can simply close
            tryDismiss(DismissTrigger.Finished);
          case 6:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }));
    return function (_x3) {
      return _ref3.apply(this, arguments);
    };
  }(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [tryDismiss, trySetCurrentStep]);

  // Start the auto disappear when we are finished
  useEffect(function () {
    // Already dismissed
    if (isDismissedRef.current) {
      return;
    }

    // Timeout already scheduled
    if (autoDisappearTimeoutRef.current) {
      return;
    }
    if (['SIGN_UP_SUCCESS', 'POST_SURVEY_NO_CONSENT', 'POST_SURVEY_HAS_SIGN_UP'].includes(currentStep)) {
      autoDisappearTimeoutRef.current = window.setTimeout(function () {
        return tryDismiss(DismissTrigger.AutoDismiss);
      }, AUTO_DISAPPEAR_DURATION);
    }
  }, [currentStep, tryDismiss]);
  useEscapeToDismiss({
    onDismiss: function onDismiss() {
      return tryDismiss(DismissTrigger.Manual);
    }
  });
  var content = function () {
    if (currentStep === 'SURVEY') {
      return /*#__PURE__*/React.createElement(SurveyForm, {
        question: question,
        statement: statement,
        textLabel: textLabel,
        textPlaceholder: textPlaceholder,
        onSubmit: onSurveySubmit
      });
    }
    if (currentStep === 'SIGN_UP_PROMPT') {
      return /*#__PURE__*/React.createElement(SignUpPrompt, {
        onAnswer: onMailingListResponse
      });
    }
    if (currentStep === 'SIGN_UP_SUCCESS') {
      return /*#__PURE__*/React.createElement(SignUpSuccess, null);
    }
    if (currentStep === 'POST_SURVEY_NO_CONSENT' || currentStep === 'POST_SURVEY_HAS_SIGN_UP') {
      return /*#__PURE__*/React.createElement(FeedbackAcknowledgement, null);
    }
    return null;
  }();
  var manualDismiss = useCallback(function () {
    // clear any pending timers
    tryClearTimeout();
    tryDismiss(DismissTrigger.Manual);
  }, [tryDismiss, tryClearTimeout]);
  return /*#__PURE__*/React.createElement(SurveyContainer, {
    onDismiss: manualDismiss
  }, content);
});